<template>
  <div class="form-field">
    <div
      v-if="field.label || field.description || isMissable"
      class="d-flex p-relative"
    >
      <div v-if="field.label || field.description">
        <label
          v-if="field.label"
          :for="fieldHtmlId(field.id)"
          class="form-field__label font-weight-semi-bold d-flex align-items-center"
          data-cy="form-label"
        >
          {{ field.label }}
          <small v-if="!field.is_required" class="text-grey-50 ml-1">
            -&nbsp;Optional
          </small>
        </label>
        <div
          v-if="field.description"
          class="text-x-small text-grey-60 font-weight-semi-bold form-field__description mb-1"
          data-cy="form-description"
        >
          {{ field.description }}
        </div>
      </div>

      <div v-if="isMissable" class="form-group__miss">
        <v-button v-if="isMissed" primary small @click="$emit('miss', false)">
          <v-icon icon="cancel" />
          Missed
        </v-button>

        <v-button v-else ghost small secondary @click="$emit('miss', true)">
          <v-icon icon="cancel" />
          Miss
        </v-button>
      </div>
    </div>

    <template v-if="field.field_type === FIELD_TYPES.ACCEPTANCE">
      <div class="form-field__input">
        <div
          data-cy="form-body"
          class="form-field__accept-body mb-2"
          v-html="field.body"
        />
        <div class="form-group">
          <v-switch
            :value="value.value"
            @input="value => handleUpdate({ value })"
          >
            <span class="font-weight-semi-bold">Accept</span>
          </v-switch>
        </div>
      </div>
    </template>

    <template v-else-if="field.field_type === FIELD_TYPES.SHORT_ANSWER">
      <div class="form-field__input">
        <v-input
          type="text"
          data-cy="form-input"
          :id="fieldHtmlId(field.id)"
          :value="isMissable && isMissed ? field.default_value : value.value"
          :disabled="isMissable && isMissed"
          @input="value => handleUpdate({ value })"
        />
      </div>
    </template>

    <template v-else-if="field.field_type === FIELD_TYPES.NUMBER">
      <div class="form-field__input">
        <v-input
          type="number"
          data-cy="form-input"
          :id="fieldHtmlId(field.id)"
          :value="isMissable && isMissed ? field.default_value : value.value"
          :disabled="isMissable && isMissed"
          @input="value => handleUpdate({ value })"
        >
          <template v-if="field.value_type" v-slot:append>
            {{ field.value_type }}
          </template>
        </v-input>
      </div>
    </template>

    <template v-else-if="field.field_type === FIELD_TYPES.LONG_ANSWER">
      <div class="form-field__input">
        <v-textarea
          data-cy="form-input"
          :id="fieldHtmlId(field.id)"
          :value="value.value"
          @input="value => handleUpdate({ value })"
        />
      </div>
    </template>

    <template v-else-if="field.field_type === FIELD_TYPES.MULTI_SELECT">
      <div class="form-field__input">
        <div class="form-group mb-3">
          <v-radio
            v-for="(option, k) in field.options"
            :current-value="value.value"
            :allow-multiple="field.allow_multiple"
            :key="k"
            :value="String(option.value)"
            @change="value => handleUpdate({ value })"
          >
            <label>{{ option.value }}</label>
          </v-radio>
        </div>
      </div>
    </template>

    <template v-else-if="field.field_type === FIELD_TYPES.IMAGE_UPLOAD">
      <v-image-input
        :value="value.value_image"
        @upload="blob => handleUpload(blob, field)"
        @remove="() => removeImage(field)"
      />
    </template>

    <template v-else-if="field.field_type === FIELD_TYPES.DATE">
      <div class="form-field__input">
        <v-date-input
          :id="fieldHtmlId(field.id)"
          :value="value.value"
          @input="value => handleUpdate({ value })"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { FIELD_TYPES } from "@/lib/form-utils"

import vSwitch from "@/components/forms/vSwitch"
import vInput from "@/components/forms/vInput"
import vTextarea from "@/components/forms/vTextarea"
import vRadio from "@/components/forms/vRadio"
import vImageInput from "@/components/forms/vImageInput"
import vDateInput from "@/components/forms/vDateInput"

export default {
  props: {
    value: { type: Object, required: true },
    field: { type: Object, required: true },
    isMissed: { type: Boolean, default: false },
    isMissable: { type: Boolean, default: false }
  },

  components: {
    vSwitch,
    vInput,
    vTextarea,
    vRadio,
    vImageInput,
    vDateInput
  },

  data() {
    return {
      FIELD_TYPES
    }
  },

  methods: {
    fieldHtmlId(fieldId) {
      return `form-field-id-${fieldId}`
    },

    removeImage() {
      this.handleUpdate({ image: null })
    },

    handleUpload(blob) {
      this.handleUpdate({ image: blob.signed_id })
    },

    handleUpdate(value) {
      this.$emit("input", {
        ...this.value,
        ...value
      })
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.form-field__input {
  position: relative;
  margin-top: $size--3;
}

.form-field__accept-body {
  font-size: $font-size--small;
  padding: $size--1 $size--4 !important;
  border: 2px solid $color-ui--grey-20;
  border-radius: var(--border-radius);
  max-height: 50vh;
  overflow: auto;
}

.form-field__description {
  margin-top: $size--1;
}

.page--material__content {
  .form-field__accept-body {
    p {
      margin: 0.5rem 0;
    }
  }
}
</style>
